import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetCPGTransactionListData,
  GetCPGTransactionListResponse,
} from "@services/models/Transactions/GetCPGTransactionListModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ConstantFunctions from "../../../utils/constants_functions";
import { CSV_EXPORT_CONSTANTS } from "../../../utils/csvExportConstants";
import { CurrencyAmountRenderer } from "./components/CurrencyAmountRenderer";
import { ReceivedAmountRenderer } from "./components/ReceivedAmountRenderer";
import { StatusPillRenderer } from "./components/StatusPillRenderer";
import CPGDetailsTabs from "./CPGDetailsTabs";
import { useCpgTransactionListSocket } from "./useCpgTransactionUpdates";

const getCpgAgRowClass = (params: { data?: GetCPGTransactionListData }) => {
  const status = params.data?.status?.toLowerCase();
  if (status === "expired" || status === "cancelled") return "ag-row-expired";
  if (
    status === "completed" ||
    status === "settled" ||
    status === "over payment" ||
    (status === "under payment" && params.data?.isFinal === 1)
  ) {
    return "ag-row-completed";
  }
  return "";
};

const CPGOrderFlow = () => {
  const constantFunctions = new ConstantFunctions();
  const transactionStatusClass = new TransactionStatusClass();
  const gridRef = useRef<AgGridReact>(null);
  const loadingRef = useRef<boolean>(false);

  const [cpgData, setCpgData] = useState<GetCPGTransactionListData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 50;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  // Column definitions for ag-grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "tdate",
        flex: 0.5,
        minWidth: 70,
        maxWidth: 80,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Trx ID",
        field: "trxnId",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 130,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[10px] text-text-base font-medium truncate">
                {data.trxnId}
              </span>
              <CopyButton
                copyText={data.trxnId.toString()}
                copyMessage="Transaction ID copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Trx Type",
        field: "transactionType",
        flex: 1.2,
        minWidth: 150,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: () => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }),
        cellRenderer: ({ data }: any) => {
          const categoryStyle = transactionStatusClass.getTransactionStatusIcon(
            data.transactionType?.toLowerCase(),
          );

          return (
            <div className="w-full flex justify-start">
              <div
                className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${categoryStyle.bgColor}`}
                style={{ color: categoryStyle.textColor }}
              >
                {data?.transactionType?.toUpperCase() || "-"}
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Link",
        field: "orderId",
        flex: 0.5,
        minWidth: 72,
        maxWidth: 88,
        sortable: false,
        filter: false,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
        cellRenderer: ({ data }: { data: GetCPGTransactionListData }) => {
          if (data?.transactionType !== "Merchant Payin") {
            return (
              <span className="text-[10px] text-text-base font-medium">-</span>
            );
          }

          const paymentUrl = `https://app.tylt.money/pscreen/${data.orderId}`;

          return (
            <div
              data-ignore-row-click="true"
              className="flex items-center justify-center gap-1"
              onClick={(event) => event.stopPropagation()}
            >
              <a
                href={paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open payment link"
                aria-label="Open payment link"
                className="flex justify-center items-center cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] bg-bg-page-body border border-bg-button-secondary text-text-base"
                style={{ width: "20px", height: "20px", borderRadius: "4px" }}
                onClick={(event) => event.stopPropagation()}
              >
                <span className="material-symbols-outlined text-[14px]">
                  open_in_new
                </span>
              </a>
              <CopyButton
                copyText={paymentUrl}
                copyMessage="Payment link copied!"
                copyIconSize={14}
                buttonSize="20px"
                borderRadius="4px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Merchant",
        field: "merchantName",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Network",
        field: "networkSymbol",
        flex: 0.8,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex flex-row gap-1 items-center">
              <img
                src={data.networkImage}
                alt={data.network}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {data.networkSymbol}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Base Amt.",
        field: "baseAmount",
        flex: 0.8,
        minWidth: 110,
        maxWidth: 150,
        cellRenderer: CurrencyAmountRenderer,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellClass: "ag-cell-custom",
        headerClass: "ag-header-cell-custom header-left",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Req Amt.",
        field: "settledAmountRequested",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: CurrencyAmountRenderer,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellClass: "ag-cell-custom",
        headerClass: "ag-header-cell-custom header-left",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Rec Amt.",
        field: "receivedAmount",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: ReceivedAmountRenderer,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellClass: "ag-cell-custom",
        headerClass: "ag-header-cell-custom header-left",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Compliance",
        field: "compliancePending",
        flex: 1,
        minWidth: 100,
        maxWidth: 160,
        sortable: false,
        filter: false,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Pending",
        field: "pending",
        flex: 0.7,
        minWidth: 80,
        maxWidth: 110,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Over",
        field: "overPayment",
        flex: 0.5,
        minWidth: 65,
        maxWidth: 90,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Under",
        field: "underPayment",
        flex: 0.6,
        minWidth: 75,
        maxWidth: 100,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Paid",
        field: "paid",
        flex: 0.6,
        minWidth: 70,
        maxWidth: 90,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Cancelled",
        field: "cancelled",
        flex: 0.8,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Expired",
        field: "expired",
        flex: 0.7,
        minWidth: 80,
        maxWidth: 110,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
    ],
    [constantFunctions],
  );

  const [transactionId, setTransactionId] = useState<number>();
  const [transactionData, setTransactionData] =
    useState<GetCPGTransactionListData>();

  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterPayinPayout, setFilterPayinPayout] = useState<
    string | undefined
  >();
  const [filterCompliancePending, setFilterCompliancePending] =
    useState<boolean>(false);

  useCpgTransactionListSocket({
    setCpgData,
    filterStatus,
    filterPayinPayout,
    filterCompliancePending,
    appliedSearchTerm,
  });

  const fetchCPGData = useCallback(
    async (pageNumber = 1, append = false, search?: string) => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);
        const response: GetCPGTransactionListResponse =
          await transactionApiServices.getCPGTransactionList({
            page: pageNumber,
            rows: rowsPerPage,
            searchValue: search,
            status: filterStatus?.toLowerCase(),
            isPaying: filterPayinPayout === "Payin" ? "cr" : undefined,
            isPayout: filterPayinPayout === "Payout" ? "dr" : undefined,
            compliancePending: filterCompliancePending ? 1 : undefined,
          });

        if (append) {
          setCpgData((prev) => [...prev, ...response.data]);
        } else {
          setCpgData(response.data);
        }
        setHasMore(response.data.length === rowsPerPage);
      } catch (err: any) {
        setError("Failed to fetch cashier data");
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [filterCompliancePending, filterPayinPayout, filterStatus],
  );

  const resetListState = useCallback(() => {
    setCpgData([]);
    setPage(1);
    setHasMore(true);
  }, []);

  useEffect(() => {
    resetListState();
    fetchCPGData(1, false, appliedSearchTerm || undefined);
  }, [
    appliedSearchTerm,
    filterCompliancePending,
    filterPayinPayout,
    filterStatus,
    fetchCPGData,
    resetListState,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm === appliedSearchTerm) {
      resetListState();
      fetchCPGData(1, false, normalizedSearchTerm || undefined);
      return;
    }

    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, fetchCPGData, resetListState, searchTerm]);

  const handleClearSearch = useCallback(() => {
    if (!searchTerm && !appliedSearchTerm) {
      return;
    }

    setSearchTerm("");

    if (appliedSearchTerm === "") {
      resetListState();
      fetchCPGData(1, false);
      return;
    }

    setAppliedSearchTerm("");
  }, [appliedSearchTerm, fetchCPGData, resetListState, searchTerm]);

  const loadMoreData = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchCPGData(nextPage, true, appliedSearchTerm || undefined);
    }
  }, [appliedSearchTerm, fetchCPGData, hasMore, page]);

  const onRowClicked = (event: RowClickedEvent) => {
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest("[data-ignore-row-click]")) {
      return;
    }

    const selectedData = event.data as GetCPGTransactionListData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setTransactionId(selectedData.trxnId);
    setTransactionData(selectedData);
    setIsPanelCollapsed(false);
  };

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreData();
      }
    },
    [hasMore, loading, loadMoreData],
  );

  const exportToCsv = async () => {
    try {
      setExportLoading(true);

      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allTransactions: GetCPGTransactionListData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const response: GetCPGTransactionListResponse =
          await transactionApiServices.getCPGTransactionList({
            page: i,
            rows: ROWS_PER_PAGE,
            searchValue: appliedSearchTerm,
            status: filterStatus?.toLowerCase(),
            isPaying: filterPayinPayout === "Payin" ? "cr" : undefined,
            isPayout: filterPayinPayout === "Payout" ? "dr" : undefined,
            compliancePending: filterCompliancePending ? 1 : undefined,
          });

        allTransactions = [...allTransactions, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allTransactions.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allTransactions = allTransactions.slice(0, MAX_ROWS);

      // Create CSV content
      const headers = [
        "Date",
        "Transaction ID",
        "Transaction Type",
        "Merchant",
        "Network",
        "Base Amount",
        "Requested Amount",
        "Received/Sent Amount",
        "Commission",
        "From Address",
        "To Address",
        "Transaction Hash",
        "Ledger",
        "Status",
      ];

      let csvContent = headers.join(",") + "\n";

      allTransactions.forEach((transaction) => {
        const row = [
          constantFunctions.dateFormatInDDMMYY(transaction.tdate),
          transaction.trxnId,
          `"${(transaction.transactionType || "").replace(/"/g, '""')}"`,
          `"${(transaction.merchantName || "").replace(/"/g, '""')}"`,
          transaction.networkSymbol,
          constantFunctions.formatAmount(transaction.baseAmount) +
            " " +
            transaction.fcs,
          constantFunctions.formatAmount(transaction.settledAmountRequested) +
            " " +
            transaction.tcs,
          transaction.ledger === "CR"
            ? constantFunctions.formatAmount(
                transaction.settledAmountReceived,
              ) +
              " " +
              transaction.tcs
            : constantFunctions.formatAmount(transaction.settledAmountSent) +
              " " +
              transaction.tcs,
          transaction.settledAmountReceived === 0
            ? 0
            : Number(transaction.commissionAmount) + " " + transaction.tcs,
          `"${transaction.fromAddress || ""}"`,
          `"${transaction.toAddress || ""}"`,
          `"${transaction.transactionHash || ""}"`,
          transaction.ledger === "CR" ? "Credit" : "Debit",
          transaction.status,
        ];

        csvContent += row.join(",") + "\n";
      });

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      link.setAttribute("download", `cpg-transactions-${formattedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export data", error);
      setError("Failed to export transaction data");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex flex-grow justify-between items-center w-full mr-4">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            CPG Order Flow
          </p>
          <LightCustomButton
            text={"EXPORT CSV"}
            isActive={true}
            onClick={exportToCsv}
            isButtonLoading={exportLoading}
          />
        </div>
      </div>
      <div className="flex flex-row items-start">
        <div
          className={`flex flex-col bg-bg-page-body py-[20px] px-[12px] overflow-hidden transition-all duration-300 ${
            transactionId && !isPanelCollapsed ? "w-3/4" : "w-full"
          }`}
          style={{ height: "calc(100dvh - 70px)" }}
        >
          {/* Filter Row */}
          <div className="flex flex-row items-center justify-between mb-4">
            <div className="flex justify-start space-x-3 items-center">
              <p className="text-text-base text-[10px] font-bold">Filter by:</p>
              <div className="flex space-x-3">
                {[
                  "Completed",
                  "Expired",
                  "Cancelled",
                  "Under payment",
                  "Over payment",
                ].map((type) => (
                  <div
                    key={type}
                    onClick={
                      loading
                        ? undefined
                        : () =>
                            setFilterStatus((prev) =>
                              prev === type ? undefined : type,
                            )
                    }
                    className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      filterStatus === type
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {type}
                  </div>
                ))}
              </div>
              <div
                style={{
                  height: "20px",
                  width: "2px",
                  backgroundColor: "#ccc",
                }}
              ></div>
              <div className="flex space-x-3">
                {["Payin", "Payout"].map((type) => (
                  <div
                    key={type}
                    onClick={
                      loading
                        ? undefined
                        : () =>
                            setFilterPayinPayout((prev) =>
                              prev === type ? undefined : type,
                            )
                    }
                    className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      filterPayinPayout === type
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {type}
                  </div>
                ))}
              </div>
              <div
                style={{
                  height: "20px",
                  width: "2px",
                  backgroundColor: "#ccc",
                }}
              />
              <div
                onClick={
                  loading
                    ? undefined
                    : () => setFilterCompliancePending((prev) => !prev)
                }
                className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                  loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                } ${
                  filterCompliancePending
                    ? "border-border-button-secondary bg-bg-button-secondary"
                    : "border-border-subtle bg-bg-page-body"
                }`}
              >
                Compliance
              </div>
            </div>
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                disabled={loading}
                isSearchApplied={Boolean(appliedSearchTerm)}
                placeholder="Search by UID / MOID / MID / Name / Hash / Address"
              />
            </div>
          </div>

          {loading && cpgData.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={cpgData}
              columnDefs={columnDefs}
              onRowClicked={onRowClicked}
              onBodyScroll={onBodyScroll}
              getRowId={(params) => params.data.trxnId.toString()}
              getRowClass={getCpgAgRowClass}
              error={error}
            />
          )}
        </div>

        {transactionId && transactionData && (
          <div
            className={`bg-bg-page-body py-4 border-l border-border-subtle transition-all duration-300 relative ${
              isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
            style={{ height: "calc(100dvh - 70px)" }}
          >
            <button
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
              style={{
                left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={isPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                {isPanelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>

            <div
              className={`overflow-auto h-full transition-opacity duration-300 ${
                isPanelCollapsed
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <CPGDetailsTabs
                transactionId={transactionId}
                merchantOrderId={transactionData.merchantOrderId}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CPGOrderFlow;
